import React, { useState, useMemo, useCallback } from 'react';
import { FireConfig, IncomeStream, Liability, ManualAsset, FireStyle, NetWorthSnapshot } from '../types';
import { Flame, Target, Settings2, DollarSign, Save, Info, Plus, Trash2, Heart, Sparkles, RefreshCw, Briefcase, PieChart as PieChartIcon, Wallet, TrendingUp, Building, CreditCard, PiggyBank, History, Sliders, CalendarClock, Shield } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, Cell, BarChart, Bar, CartesianGrid, LabelList, LineChart, Line } from 'recharts';
import { analyzeFirePlan } from '../services/geminiService';
import { CanonicalNetWorthBreakdown, buildCanonicalNetWorth } from '../utils/netWorth';
import {
  computeDividendProjection,
  computeFireTargets,
  computeFireWealthBreakdown,
  computeIncomeStreamAmountAtAge,
  computeRequiredMonthlySavings,
  computeRetirementSnapshotBreakdown,
  computeRetirementReadiness,
  computeSecureIncomeAtAge,
} from '../utils/fireMetrics';

interface FireSnapshotProps {
  portfolioValue: number;
  portfolioCashValue: number;
  passiveIncome: number; // Projected annual (Dividends)
  currencySymbol: string;
  config: FireConfig;
  onUpdateConfig: (config: FireConfig) => void;
}

type Tab = 'overview' | 'strategy' | 'networth' | 'income';

const COLORS = ['#38BDF8', '#0EA5E9', '#0284C7', '#0369A1', '#7DD3FC', '#22C55E', '#F59E0B', '#EF4444'];

const FireSnapshot: React.FC<FireSnapshotProps> = ({
  portfolioValue,
  portfolioCashValue,
  passiveIncome,
  currencySymbol,
  config,
  onUpdateConfig,
}) => {
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [tempConfig, setTempConfig] = useState<FireConfig>(config);

  // Playground / Simulation State
  const [simSavingsAdj, setSimSavingsAdj] = useState(0); // +/- Monthly
  const [simRetirementAdj, setSimRetirementAdj] = useState(0); // +/- Years
  const [simReturnRateAdj, setSimReturnRateAdj] = useState(0); // +/- %

  // AI State
  const [aiAnalysis, setAiAnalysis] = useState<string | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  React.useEffect(() => {
    setTempConfig(config);
  }, [config]);

  const netWorth: CanonicalNetWorthBreakdown = useMemo(
      () => buildCanonicalNetWorth(tempConfig, portfolioValue),
      [tempConfig, portfolioValue]
  );

  const fireWealth = useMemo(
      () => computeFireWealthBreakdown(tempConfig, portfolioValue),
      [tempConfig, portfolioValue]
  );

  const currentNetWorth = fireWealth.totalNetWorthToday;
  const safeWithdrawalAmount = fireWealth.liquidFireAssetsToday * (tempConfig.withdrawalRate / 100);
  const totalAssetsValue = fireWealth.totalAssetsToday;
  const hasFireAssets = tempConfig.manualAssets.length + tempConfig.pensions.length > 0;

  // --- Analytical Data Preparation ---

  // 1. Wealth Drivers (Projected Annual Growth)
  const wealthDrivers = useMemo(() => {
      // Portfolio Growth
      const portGrowth = portfolioValue * (tempConfig.investmentReturnRate / 100);

      // Manual Assets Growth
      const otherGrowth = tempConfig.manualAssets.map(a => ({
          name: a.name,
          growthAmount: a.value * (a.growthRate / 100),
          growthRate: a.growthRate
      }));

      return [
          { name: 'Portfolio Growth', amount: portGrowth, rate: tempConfig.investmentReturnRate, isPassive: true },
          ...otherGrowth.map(g => ({ name: g.name, amount: g.growthAmount, rate: g.growthRate, isPassive: true }))
      ].sort((a, b) => b.amount - a.amount);
  }, [portfolioValue, tempConfig, passiveIncome]);


  // --- Simulation Engine ---
  const simulationData = useMemo(() => {
    const data = [];
    const {
        currentAge,
        retirementAge,
        lifeExpectancy,
        annualExpenses,
        retirementExpenses,
        investmentReturnRate,
        inflationRate,
        incomeGrowthRate,
        incomeStreams,
        manualAssets,
        liabilities
    } = tempConfig;

    // Use retirement expenses if defined, otherwise fallback to annualExpenses
    const targetRetirementExpenses = retirementExpenses || annualExpenses;

    // Apply Simulation Modifiers
    const effectiveRetirementAge = retirementAge + simRetirementAdj;
    const effectiveReturnRate = (investmentReturnRate + simReturnRateAdj) / 100;

    // Split assets into FIRE-ready liquid vs illiquid buckets (home/pension excluded from liquid progress).
    const liquidManualAssets = manualAssets.filter(
        a => a.type === 'Cash' || a.type === 'Crypto' || a.type === 'ISA' || a.type === 'GIA'
    );
    const illiquidManualAssets = manualAssets.filter(
        a => a.type !== 'Cash' && a.type !== 'Crypto' && a.type !== 'ISA' && a.type !== 'GIA'
    );

    const cashLikeAssets = liquidManualAssets.reduce((sum, a) => sum + a.value, 0);
    const liquidManualValue = cashLikeAssets;
    const illiquidManualValue = illiquidManualAssets.reduce((sum, a) => sum + a.value, 0);

    const liquidManualGrowthRate = cashLikeAssets > 0
        ? liquidManualAssets.reduce((sum, a) => sum + a.value * ((a.growthRate || 0) / 100), 0) / cashLikeAssets
        : 0;

    const illiquidGrowthRate = illiquidManualValue > 0
        ? illiquidManualAssets.reduce((sum, a) => sum + a.value * ((a.growthRate || 3) / 100), 0) / illiquidManualValue
        : 0.03; // default to 3% if nothing provided

    // Baseline Start
    let portfolioBalance = portfolioValue;
    let manualLiquidBalance = cashLikeAssets;

    // Treat non-cash assets (e.g. Home, Other) as illiquid
    let illiquidAssets = manualAssets
      .filter(a => a.type !== 'Cash' && a.type !== 'Crypto' && a.type !== 'ISA' && a.type !== 'GIA')
      .reduce((sum, a) => sum + a.value, 0);

    const liabilityBalances = liabilities.map((l) => l.balance);
    let debtBalance = liabilityBalances.reduce((sum, balance) => sum + balance, 0);

    // Create a parallel "Baseline" track without sliders for comparison
    const baseLiabilityBalances = liabilities.map((l) => l.balance);
    let basePortfolio = portfolioValue;
    let baseManualLiquid = liquidManualValue;
    let baseIlliquid = illiquidAssets;
    let baseDebt = baseLiabilityBalances.reduce((sum, balance) => sum + balance, 0);

    const currentInflation = inflationRate / 100;
    let insolvencyAge: number | null = null;
    let insolvencyDeficit = 0;
    let baseInsolvencyDeficit = 0;

    for (let age = currentAge; age <= lifeExpectancy; age++) {
        const yearIndex = age - currentAge;
        const isRetired = age >= effectiveRetirementAge;
        const isBaseRetired = age >= retirementAge;

        // --- Dual Expense Logic (Pre vs Post Retirement) ---
        // We use annualExpenses for "Accumulation Phase" and retirementExpenses for "Distribution Phase"
        const baseExpenseAmount = isRetired ? targetRetirementExpenses : annualExpenses;
        const yearlyExpenses = baseExpenseAmount * Math.pow(1 + currentInflation, yearIndex);

        // Baseline (no sim) logic
        const baseExpenseAmountRef = isBaseRetired ? targetRetirementExpenses : annualExpenses;

        // --- Simulated Income ---
        let yearlyIncome = 0;
        incomeStreams.forEach(stream => {
            if (age >= stream.startAge && age < stream.endAge) {
                yearlyIncome += computeIncomeStreamAmountAtAge(stream, age, currentAge, incomeGrowthRate);
            }
        });
        // Add Simulator Savings adjustment (only if not retired yet)
        if (!isRetired) {
            yearlyIncome += (simSavingsAdj * 12);
        }

        // --- Baseline Income (No sliders) ---
        let baseYearlyIncome = 0;
        incomeStreams.forEach(stream => {
            if (age >= stream.startAge && age < stream.endAge) {
                baseYearlyIncome += computeIncomeStreamAmountAtAge(stream, age, currentAge, incomeGrowthRate);
            }
        });

        // Debt Payments (use each liability's APR)
        let annualDebtPay = 0;

        liabilities.forEach((l, index) => {
          const annualPayment = l.monthlyPayment * 12;
          annualDebtPay += annualPayment;

          const balance = liabilityBalances[index];
          if (balance > 0) {
            const interest = balance * (l.interestRate / 100);
            liabilityBalances[index] = Math.max(0, balance - annualPayment + interest);
          }

          const baseBalance = baseLiabilityBalances[index];
          if (baseBalance > 0) {
            const baseInterest = baseBalance * (l.interestRate / 100);
            baseLiabilityBalances[index] = Math.max(0, baseBalance - annualPayment + baseInterest);
          }
        });

        debtBalance = liabilityBalances.reduce((sum, balance) => sum + balance, 0);
        baseDebt = baseLiabilityBalances.reduce((sum, balance) => sum + balance, 0);

        // Net Cash Flow
        let netCashFlow = yearlyIncome - yearlyExpenses - annualDebtPay;
        let baseNetCashFlow = baseYearlyIncome - (baseExpenseAmountRef * Math.pow(1 + currentInflation, yearIndex)) - annualDebtPay;

        // Growth by asset bucket
        portfolioBalance = portfolioBalance * (1 + effectiveReturnRate);
        manualLiquidBalance = manualLiquidBalance * (1 + liquidManualGrowthRate);
        basePortfolio = basePortfolio * (1 + (investmentReturnRate/100));
        baseManualLiquid = baseManualLiquid * (1 + liquidManualGrowthRate);

        illiquidAssets *= (1 + illiquidGrowthRate);
        baseIlliquid *= (1 + illiquidGrowthRate);

        // Apply Cash Flow, selling assets if liquid assets are exhausted
        if (netCashFlow >= 0) {
            portfolioBalance += netCashFlow;
        } else {
            portfolioBalance += netCashFlow;
            if (portfolioBalance < 0) {
                manualLiquidBalance += portfolioBalance;
                portfolioBalance = 0;
            }
            if (manualLiquidBalance < 0) {
                illiquidAssets += manualLiquidBalance;
                manualLiquidBalance = 0;
            }
            if (illiquidAssets < 0) {
                insolvencyDeficit += illiquidAssets;
                illiquidAssets = 0;
            }
        }

        if (insolvencyAge === null && insolvencyDeficit < 0) {
            insolvencyAge = age;
        }

        // Apply Baseline Cash Flow
        if (baseNetCashFlow >= 0) {
            basePortfolio += baseNetCashFlow;
        } else {
            basePortfolio += baseNetCashFlow;
            if (basePortfolio < 0) {
                baseManualLiquid += basePortfolio;
                basePortfolio = 0;
            }
            if (baseManualLiquid < 0) {
                baseIlliquid += baseManualLiquid;
                baseManualLiquid = 0;
            }
            if (baseIlliquid < 0) {
                baseInsolvencyDeficit += baseIlliquid;
                baseIlliquid = 0;
            }
        }
        const totalLiquid = portfolioBalance + manualLiquidBalance;
        const baseTotalLiquid = basePortfolio + baseManualLiquid;

        data.push({
            age,
            balance: Math.round(totalLiquid + illiquidAssets + insolvencyDeficit - debtBalance),
            liquid: Math.round(totalLiquid),
            manualLiquid: Math.round(Math.max(0, manualLiquidBalance)),
            portfolioBalance: Math.max(0, portfolioBalance),
            baseBalance: Math.round(baseTotalLiquid + baseIlliquid + baseInsolvencyDeficit - baseDebt), // Track baseline for comparison
            expenses: Math.round(yearlyExpenses),
            income: Math.round(yearlyIncome),
            debtBalance: Math.round(debtBalance),
            illiquidAssets: Math.round(illiquidAssets),
            insolvent: insolvencyAge !== null && age >= insolvencyAge,
            isRetired
        });
    }
    return data;
  }, [tempConfig, portfolioValue, simSavingsAdj, simRetirementAdj, simReturnRateAdj]);

  const computeIncomeSnapshot = useCallback((age: number, options: {
      includeWithdrawals?: boolean;
      includeDividends?: boolean;
      suppressSalaryEndingAtRetirement?: boolean;
  } = {}) => {
      const {
          includeWithdrawals = true,
          includeDividends = true,
          suppressSalaryEndingAtRetirement = false
      } = options;

      const retirementBoundaryAge = tempConfig.retirementAge + simRetirementAdj;
      const snapshot = simulationData.find(d => d.age === age);
      if (!snapshot) return null;

      // Portfolio-based income (dividends + SWR drawdown when enabled)
      const portfolioBalance = Math.max(0, snapshot.portfolioBalance ?? 0);
      const liquidBalance = Math.max(0, snapshot.liquid);
      const safeWithdrawalLimit = includeWithdrawals ? liquidBalance * (tempConfig.withdrawalRate / 100) : 0;

      const dividendProjection = computeDividendProjection({
          portfolioValue,
          passiveIncome,
          projectedPortfolioBalance: portfolioBalance,
          includeDividends,
          includeWithdrawals,
          safeWithdrawalLimit,
      });
      const projectedDividends = dividendProjection.projectedDividends;

      const dividendPortion = includeDividends ? projectedDividends : 0;
      const capitalSalePortion = includeWithdrawals ? Math.max(0, safeWithdrawalLimit - projectedDividends) : 0;

      const portfolioSources = [] as { name: string; amount: number; type: string; color: string }[];

      if (dividendPortion > 0) {
          portfolioSources.push({
              name: 'Projected Dividends',
              amount: dividendPortion,
              type: 'Passive',
              color: '#10b981'
          });
      }

      if (capitalSalePortion > 0) {
          portfolioSources.push({
              name: 'Safe Capital Withdrawals',
              amount: capitalSalePortion,
              type: 'Drawdown',
              color: '#6366f1'
          });
      }

      // Fixed income streams active at this age
      const activeStreams = tempConfig.incomeStreams.filter(stream => {
          const isActive = age >= stream.startAge && age < stream.endAge;
          if (!isActive) return false;

          const salaryEndsByRetirement = suppressSalaryEndingAtRetirement
              && stream.type === 'Salary'
              && stream.endAge <= retirementBoundaryAge;

          if (salaryEndsByRetirement && age >= retirementBoundaryAge) {
              return false;
          }

          return true;
      });

      const fixedIncomeSources = activeStreams.map(stream => ({
          name: stream.name,
          amount: computeIncomeStreamAmountAtAge(stream, age, tempConfig.currentAge, tempConfig.incomeGrowthRate),
          type: stream.type,
          color: ''
      }));

      const allSources = [
          ...portfolioSources,
          ...fixedIncomeSources
      ].map((source, index) => ({
          ...source,
          color: source.color || COLORS[(index + 2) % COLORS.length]
      }));

      const totalIncome = allSources.reduce((sum, source) => sum + source.amount, 0);
      const projectedExpenses = snapshot.expenses;

      return {
          totalIncome,
          projectedExpenses,
          sources: allSources.sort((a, b) => b.amount - a.amount),
          surplus: totalIncome - projectedExpenses,
          dividendProjection,
          age
      };
  }, [simulationData, tempConfig, simRetirementAdj, passiveIncome, portfolioValue]);

  const preRetirementPaycheck = useMemo(() => {
      const targetAge = tempConfig.retirementAge + simRetirementAdj;
      const previewAge = Math.max(tempConfig.currentAge, targetAge - 1);
      return computeIncomeSnapshot(previewAge, {
          includeWithdrawals: false,
          includeDividends: true,
          suppressSalaryEndingAtRetirement: false
      });
  }, [computeIncomeSnapshot, tempConfig.currentAge, tempConfig.retirementAge, simRetirementAdj]);

  const retirementPaycheck = useMemo(() => {
      const targetAge = tempConfig.retirementAge + simRetirementAdj;
      return computeIncomeSnapshot(targetAge, {
          includeWithdrawals: true,
          includeDividends: true,
          suppressSalaryEndingAtRetirement: true
      });
  }, [computeIncomeSnapshot, tempConfig.retirementAge, simRetirementAdj]);


  // Metrics
  const targetRetirementSpend = tempConfig.retirementExpenses || tempConfig.annualExpenses;
  const fireTargets = computeFireTargets(tempConfig, tempConfig.retirementAge + simRetirementAdj);
  const fireTargetTooltip =
    tempConfig.withdrawalRate > 0
      ? `Your FIRE target is calculated as: FIRE Number = Annual Retirement Spend ÷ Withdrawal Rate. With ${currencySymbol}${fireTargets.retirementExpensesToday.toLocaleString(undefined, { maximumFractionDigits: 0 })} per year and a ${tempConfig.withdrawalRate}% safe withdrawal rate (${(tempConfig.withdrawalRate / 100).toLocaleString(undefined, { maximumFractionDigits: 4 })}), today's FIRE target is ${currencySymbol}${fireTargets.fireNumberToday.toLocaleString(undefined, { maximumFractionDigits: 0 })}. It only uses your retirement spending input and the safe withdrawal rate.`
      : 'Set a safe withdrawal rate above 0% to calculate your FIRE target (Annual Retirement Spend ÷ Withdrawal Rate).';

  const retirementAgeTarget = tempConfig.retirementAge + simRetirementAdj;
  const retirementSnapshot = useMemo(() => {
      if (simulationData.length === 0) return undefined;
      return simulationData.reduce<typeof simulationData[number] | undefined>((closest, entry) => {
          if (!closest) return entry;
          const entryDiff = Math.abs(entry.age - retirementAgeTarget);
          const closestDiff = Math.abs(closest.age - retirementAgeTarget);
          return entryDiff < closestDiff ? entry : closest;
      }, undefined);
  }, [retirementAgeTarget, simulationData]);
  const retirementLiquid = retirementSnapshot?.liquid ?? 0;
  const retirementPortfolio = Math.max(0, retirementSnapshot?.portfolioBalance ?? 0);
  const retirementManualLiquid = Math.max(0, retirementLiquid - retirementPortfolio);
  const portfolioCashShare = portfolioValue > 0 ? Math.min(1, Math.max(0, portfolioCashValue / portfolioValue)) : 0;
  const retirementPortfolioInvested = Math.max(0, retirementPortfolio - Math.max(0, retirementPortfolio * portfolioCashShare));
  const yearsToRetirement = Math.max(0, retirementAgeTarget - tempConfig.currentAge);
  const retirementBreakdown = computeRetirementSnapshotBreakdown({
      projectedLiquidFireAssetsAtRetirement: retirementLiquid,
      manualAssets: tempConfig.manualAssets,
      liabilities: tempConfig.liabilities,
      yearsToRetirement,
  });
  const retirementNetWorth = retirementBreakdown.projectedTotalNetWorthAtRetirement;
  const retirementOtherAssets = retirementBreakdown.projectedOtherIlliquidAssetsAtRetirement;
  const retirementDebt = retirementBreakdown.projectedLiabilitiesAtRetirement;
  const projectedPensionAssetsAtRetirement = retirementBreakdown.projectedPensionAssetsAtRetirement;
  const projectedPropertyAssetsAtRetirement = retirementBreakdown.projectedPropertyAssetsAtRetirement;

  const yearsToTargetFromSimulation = useMemo(() => {
      const projectedHit = simulationData.find((entry) => entry.liquid >= fireTargets.fireNumberAtRetirement);
      if (!projectedHit) return null;
      return Math.max(0, projectedHit.age - tempConfig.currentAge);
  }, [simulationData, fireTargets.fireNumberAtRetirement, tempConfig.currentAge]);

  const fireNumberAtRetirement = fireTargets.fireNumberAtRetirement;
  const liquidFireProgressPct = fireTargets.fireNumberToday > 0 ? (fireWealth.liquidFireAssetsToday / fireTargets.fireNumberToday) * 100 : 0;
  const totalNetWorthProgressPct = fireTargets.fireNumberToday > 0 ? (currentNetWorth / fireTargets.fireNumberToday) * 100 : 0;

  // Consider "run out of money" when liquid assets are depleted
  const bankruptcyAge = simulationData.find(d => d.insolvent)?.age ?? simulationData.find(d => d.balance < 0)?.age;
  const wealthAtRetirementLiquid = retirementLiquid;

  // Motivation Engine
  const secureIncomeAtRetirement = computeSecureIncomeAtAge(
      tempConfig.incomeStreams,
      retirementAgeTarget,
      tempConfig.currentAge,
      tempConfig.incomeGrowthRate
  );
  const retirementReadiness = computeRetirementReadiness({
      projectedTotalNetWorthAtRetirement: retirementNetWorth,
      projectedLiquidFireAssetsAtRetirement: wealthAtRetirementLiquid,
      projectedRetirementExpensesAtRetirement: fireTargets.projectedRetirementExpensesAtRetirementAge,
      fireNumberAtRetirement,
      secureIncomeAtRetirement,
      withdrawalRate: tempConfig.withdrawalRate,
  });
  const gapToTarget = Math.max(0, fireNumberAtRetirement - wealthAtRetirementLiquid);
  const requiredExtraMonthly = computeRequiredMonthlySavings(
      gapToTarget,
      yearsToRetirement,
      tempConfig.investmentReturnRate + simReturnRateAdj
  );
  const r = ((tempConfig.investmentReturnRate + simReturnRateAdj) / 100) / 12;
  const n = Math.max(1, yearsToRetirement * 12);
  const extraSavingsBuffer = r === 0 ? 500 * n : 500 * ((Math.pow(1+r, n) - 1)/r);
  const hasDebtGrowingRisk = tempConfig.liabilities.some((liability) =>
      liability.balance > 0 && liability.monthlyPayment < (liability.balance * (liability.interestRate / 100)) / 12
  );

  const handleSave = () => {
      const updated = { ...tempConfig, lastUpdated: Date.now() };
      setTempConfig(updated);
      onUpdateConfig(updated);
  };

  const handleLogSnapshot = () => {
      const newSnapshot: NetWorthSnapshot = {
          date: new Date().toISOString(),
          totalNetWorth: currentNetWorth,
          liquidAssets: fireWealth.liquidFireAssetsToday
      };

      const newHistory = [...(tempConfig.snapshots || []), newSnapshot];
      const newConfig = { ...tempConfig, snapshots: newHistory, lastUpdated: Date.now() };
      setTempConfig(newConfig);
      onUpdateConfig(newConfig);
  };

  const handleAnalyzeFire = async () => {
      const aiStudio = (window as any).aistudio;
      if (aiStudio && !await aiStudio.hasSelectedApiKey()) {
        await aiStudio.openSelectKey();
      }
      setIsAnalyzing(true);
      const summary = {
          currentPortfolioValue: portfolioValue,
          wealthAtRetirement: wealthAtRetirementLiquid, // Use liquid for conservative est
          bankruptcyAge: bankruptcyAge,
          isSustainable: retirementReadiness.isFireReady
      };
      const result = await analyzeFirePlan(tempConfig, summary);
      setAiAnalysis(result);
      setIsAnalyzing(false);
  };

  // --- Custom Tooltip Component ---
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const balanceItem = payload.find((p: any) => p.dataKey === 'balance');
      const baselineItem = payload.find((p: any) => p.dataKey === 'baseBalance');

      if (!balanceItem?.payload) return null;

      const data = balanceItem.payload;
      const netWorth = balanceItem.value;
      const isProjected = simSavingsAdj !== 0 || simRetirementAdj !== 0;

      return (
        <div className="bg-[#1e1e26] p-4 rounded-xl border border-white/10 shadow-2xl backdrop-blur-md z-50">
          <p className="text-slate-400 text-xs font-bold uppercase mb-2">Age {label}</p>

          <div className="space-y-1.5">
            <div className="flex justify-between items-center gap-6">
              <span className="text-sm text-white font-medium">
                {isProjected ? 'Simulated Total Net Worth' : 'Projected Total Net Worth'}
              </span>
              <span className={`text-sm font-bold ${netWorth < 0 ? 'text-rose-400' : 'text-emerald-400'}`}>
                {currencySymbol}{netWorth.toLocaleString()}
              </span>
            </div>

            <div className="flex justify-between items-center gap-6">
                <span className="text-xs text-slate-500">Liquid FIRE assets</span>
                <span className="text-xs text-cyan-300">{currencySymbol}{(data.liquid ?? 0).toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center gap-6">
                <span className="text-xs text-slate-500">Expenses / Income</span>
                <span className="text-xs text-slate-400">{currencySymbol}{data.expenses.toLocaleString()} / {currencySymbol}{data.income.toLocaleString()}</span>
            </div>
            {typeof data.debtBalance === 'number' && (
              <div className="flex justify-between items-center gap-6">
                <span className="text-xs text-slate-500">Debt balance</span>
                <span className="text-xs text-rose-300">{currencySymbol}{data.debtBalance.toLocaleString()}</span>
              </div>
            )}

            {baselineItem && (
              <div className="flex justify-between items-center gap-6">
                <span className="text-xs text-slate-500">Baseline</span>
                <span className="text-xs text-slate-500">
                  {currencySymbol}{baselineItem.value.toLocaleString()}
                </span>
              </div>
            )}
          </div>

          {(netWorth < 0 || data.insolvent) && (
             <div className="mt-3 pt-3 border-t border-white/10 animate-in fade-in slide-in-from-top-1">
                <div className="flex items-center text-rose-400 text-xs font-bold mb-1.5">
                    <Info size={12} className="mr-1.5" />
                    <span>Insolvency Reason</span>
                </div>
                <p className="text-slate-400 text-[10px] leading-relaxed max-w-[200px]">
                   {data.insolvent ? 'Liquid and illiquid assets depleted. ' : ''}
                   Expenses (<span className="text-slate-300">{currencySymbol}{data.expenses.toLocaleString()}</span>)
                   exceeded income (<span className="text-slate-300">{currencySymbol}{data.income.toLocaleString()}</span>).
                </p>
             </div>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 pb-20">

        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
            <div>
                 <h1 className="text-2xl font-bold text-white flex items-center">
                    <Flame className="text-sky-500 mr-2" fill="currentColor" />
                    FIRE Command Center
                 </h1>
                 <p className="text-slate-400 text-sm">Plan your financial independence with precision.</p>
            </div>

            <div className="flex space-x-1 bg-[#1e1e26] p-1 rounded-lg border border-white/5">
                {(['overview', 'strategy', 'networth', 'income'] as Tab[]).map(t => (
                    <button
                        key={t}
                        onClick={() => setActiveTab(t)}
                        className={`px-4 py-2 text-sm font-medium rounded-md capitalize transition-all ${
                            activeTab === t ? 'bg-sky-600 text-white shadow-sm shadow-sky-700/20' : 'text-slate-500 hover:text-white [html.theme-light_&]:hover:text-sky-800'
                        }`}
                    >
                        {t === 'networth' ? 'Net Worth' : t}
                    </button>
                ))}
            </div>
        </div>

        {/* --- OVERVIEW TAB --- */}
        {activeTab === 'overview' && (
            <div className="space-y-6">
                {/* Metric Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Net Worth Card */}
                    <div className="bg-[#1e1e26] p-6 rounded-2xl border border-white/5 shadow-lg flex flex-col justify-between relative group">
                        <button
                            onClick={handleLogSnapshot}
                            className="absolute top-4 right-4 text-slate-500 hover:text-white p-2 hover:bg-white/5 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                            title="Log current Snapshot"
                        >
                            <History size={18} />
                        </button>
                        <div>
                            <div className="text-slate-500 text-xs font-bold uppercase mb-2">Net Worth</div>
                            <div className="text-3xl font-bold text-white mb-1">
                                {currencySymbol}{currentNetWorth.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                            </div>
                            <div className="flex items-center space-x-2 text-xs text-slate-400">
                                <span>Assets: {currencySymbol}{totalAssetsValue.toLocaleString()}</span>
                                <span className="text-slate-600">|</span>
                                <span>Liabilities: {currencySymbol}{fireWealth.totalLiabilitiesToday.toLocaleString()}</span>
                            </div>
                        </div>
                        <div className="mt-4">
                             {/* Mini History Chart */}
                            {tempConfig.snapshots && tempConfig.snapshots.length > 1 && (
                                <div className="h-10 w-full mb-2">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <LineChart data={tempConfig.snapshots}>
                                            <Line type="monotone" dataKey="totalNetWorth" stroke="#10b981" strokeWidth={2} dot={false} />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            )}
                            {hasFireAssets ? (
                                <div className="space-y-3">
                                    <div>
                                        <div className="flex justify-between text-[11px] text-slate-400 mb-1">
                                            <span>Total net worth vs FIRE target</span>
                                            <span>{totalNetWorthProgressPct.toFixed(1)}%</span>
                                        </div>
                                        <div className="w-full bg-slate-800 h-2 rounded-full overflow-hidden">
                                           <div className="bg-sky-400 h-full" style={{ width: `${Math.max(0, Math.min(100, totalNetWorthProgressPct))}%` }}></div>
                                        </div>
                                    </div>
                                    <div>
                                        <div className="flex justify-between text-[11px] text-slate-400 mb-1">
                                            <span>Liquid FIRE progress</span>
                                            <span>{liquidFireProgressPct.toFixed(2)}%</span>
                                        </div>
                                        <div className="w-full bg-slate-800 h-2 rounded-full overflow-hidden">
                                           <div className="bg-emerald-500 h-full" style={{ width: `${Math.max(0, Math.min(100, liquidFireProgressPct))}%` }}></div>
                                        </div>
                                    </div>
                                    <div className="text-right text-xs text-emerald-400 mt-1 font-medium flex items-center justify-end gap-1">
                                       <span>FIRE target today: {currencySymbol}{fireTargets.fireNumberToday.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                                       <div className="relative group">
                                         <Info size={12} className="text-slate-500 hover:text-slate-300 cursor-help transition-colors" />
                                         <div className="absolute right-0 mt-2 max-w-[20rem] bg-black/90 text-[11px] text-slate-200 p-3 rounded-lg border border-white/10 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity duration-200 pointer-events-none shadow-xl whitespace-normal break-words text-left z-20">
                                           {fireTargetTooltip} Total net worth includes home, pensions and other assets. Liquid FIRE progress only includes portfolio/cash/ISA/GIA assets that can realistically fund early retirement.
                                         </div>
                                       </div>
                                   </div>
                                    <p className="text-[11px] text-slate-400 mt-1 text-right">
                                        Liquid FIRE assets could safely fund <span className="text-slate-200 font-semibold">{currencySymbol}{safeWithdrawalAmount.toLocaleString(undefined, { maximumFractionDigits: 0 })}/year</span>.
                                    </p>
                                </div>
                            ) : (
                                <div className="mt-2 text-xs text-amber-400 text-right">
                                    Add assets to unlock accurate FIRE progress.
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Simulation / Playground Card */}
                    <div className="bg-[#1e1e26] p-6 rounded-2xl border border-white/5 shadow-lg border-l-4 border-l-sky-500">
                        <div className="flex items-center mb-4">
                            <Sliders size={16} className="text-sky-400 mr-2" />
                            <div className="text-white font-bold text-sm">Simulator Playground</div>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <div className="flex justify-between text-xs mb-1">
                                    <span className="text-slate-400">Monthly Savings</span>
                                    <span className={simSavingsAdj !== 0 ? 'text-sky-400 font-bold' : 'text-white'}>
                                        {simSavingsAdj > 0 ? '+' : ''}{currencySymbol}{simSavingsAdj.toLocaleString()}
                                    </span>
                                </div>
                                <input
                                    type="range" min="-1000" max="5000" step="100"
                                    value={simSavingsAdj}
                                    onChange={(e) => setSimSavingsAdj(+e.target.value)}
                                    className="w-full accent-sky-500 h-1 bg-slate-700 rounded-lg appearance-none cursor-pointer"
                                />
                            </div>
                            <div>
                                <div className="flex justify-between text-xs mb-1">
                                    <span className="text-slate-400">Retirement Age</span>
                                    <span className={simRetirementAdj !== 0 ? 'text-sky-400 font-bold' : 'text-white'}>
                                        {retirementAgeTarget} ({yearsToRetirement} yrs)
                                    </span>
                                </div>
                                <input
                                    type="range" min="-5" max="5" step="1"
                                    value={simRetirementAdj}
                                    onChange={(e) => setSimRetirementAdj(+e.target.value)}
                                    className="w-full accent-sky-500 h-1 bg-slate-700 rounded-lg appearance-none cursor-pointer"
                                />
                            </div>
                            <button
                                onClick={() => { setSimSavingsAdj(0); setSimRetirementAdj(0); setSimReturnRateAdj(0); }}
                                className="text-xs text-slate-500 hover:text-sky-400 w-full text-right"
                            >
                                Reset Simulator
                            </button>
                        </div>
                    </div>

                    {/* Gap Analysis / Motivation */}
                    <div className="bg-gradient-to-br from-sky-900/40 to-slate-900 p-6 rounded-2xl border border-sky-500/20 shadow-lg flex flex-col justify-between">
                        <div>
                            <div className="text-sky-300 text-xs font-bold uppercase mb-2 flex items-center">
                                <Target size={14} className="mr-1"/> Motivation
                            </div>
                            {!retirementReadiness.isFireReady ? (
                                <div>
                                    <div className="text-xl font-bold text-white mb-1">Off Track</div>
                                    <p className="text-xs text-slate-400 leading-relaxed mb-3">
                                        {bankruptcyAge
                                            ? <>Projected to run out of money at age <span className="text-rose-400 font-bold">{bankruptcyAge}</span>.</>
                                            : retirementReadiness.message}
                                    </p>
                                    <div className="p-3 bg-rose-500/10 border border-rose-500/30 rounded-lg">
                                        {gapToTarget > 0 ? (
                                            <p className="text-xs text-rose-200">
                                                Gap: To retire safely at {tempConfig.retirementAge + simRetirementAdj}, you need roughly <strong>{currencySymbol}{Math.round(requiredExtraMonthly).toLocaleString()}/mo</strong> in extra savings starting now.
                                            </p>
                                        ) : (
                                            <p className="text-xs text-rose-200">
                                                Inflated retirement FIRE target is met by projected liquid assets, but income coverage still needs review.
                                            </p>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    <div className="text-xl font-bold text-white mb-1">On Track! 🚀</div>
                                    <p className="text-xs text-slate-400 leading-relaxed">
                                        {retirementReadiness.message}
                                    </p>
                                    <div className="mt-3 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                                        <p className="text-xs text-emerald-200">
                                            Tip: Saving an extra {currencySymbol}500/mo creates a {currencySymbol}{Math.round(extraSavingsBuffer).toLocaleString()} buffer.
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-[#1e1e26] p-6 rounded-2xl border border-white/5 shadow-lg">
                        <div className="flex items-center text-slate-500 text-xs font-bold uppercase mb-2">
                            <CalendarClock size={14} className="mr-2 text-cyan-300" />
                            Years to target
                        </div>
                        <div className="text-3xl font-bold text-white mb-1">
                            {yearsToRetirement} yrs
                        </div>
                        <p className="text-xs text-slate-400">Retire at age {retirementAgeTarget}</p>
                    </div>
                    <div className="bg-[#1e1e26] p-6 rounded-2xl border border-white/5 shadow-lg">
                        <div className="flex items-center text-slate-500 text-xs font-bold uppercase mb-2">
                            <Shield size={14} className="mr-2 text-violet-300" />
                            Withdrawal plan
                        </div>
                        <div className="text-3xl font-bold text-white mb-1">
                            {tempConfig.withdrawalRate.toFixed(1)}%
                        </div>
                        <p className="text-xs text-slate-400">
                            {currencySymbol}
                            {targetRetirementSpend.toLocaleString(undefined, { maximumFractionDigits: 0 })}{' '}
                            / yr
                        </p>
                    </div>
                </div>

                {/* Snapshots: Today vs Retirement */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-[#1e1e26] p-6 rounded-2xl border border-white/5 shadow-lg flex flex-col justify-between">
                        <div>
                            <div className="text-slate-500 text-xs font-bold uppercase mb-2">Snapshot: Today</div>
                            <div className="text-3xl font-bold text-white mb-1">
                                {currencySymbol}{currentNetWorth.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                            </div>
                            <div className="flex items-center space-x-2 text-xs text-slate-400">
                                <span>Liquid: {currencySymbol}{fireWealth.liquidFireAssetsToday.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                                <span className="text-slate-600">|</span>
                                <span>Age {tempConfig.currentAge}</span>
                            </div>
                        </div>
                        <div className="mt-4">
                            <div className="mt-3 space-y-1 text-xs text-slate-400">
                                <div className="flex justify-between"><span>Total Net Worth</span><span className="text-slate-200">{currencySymbol}{currentNetWorth.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span></div>
                                <div className="flex justify-between"><span>Liquid FIRE Assets</span><span className="text-emerald-300">{currencySymbol}{fireWealth.liquidFireAssetsToday.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span></div>
                                <div className="flex justify-between"><span>Property / Home Equity</span><span className="text-slate-200">{currencySymbol}{fireWealth.propertyAssetsToday.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span></div>
                                <div className="flex justify-between"><span>Pension Assets</span><span className="text-slate-200">{currencySymbol}{fireWealth.pensionAssetsToday.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span></div>
                                <div className="flex justify-between"><span>Liabilities</span><span className="text-rose-300">{currencySymbol}{fireWealth.totalLiabilitiesToday.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span></div>
                                <div className="flex justify-between"><span>FIRE target today</span><span className="text-slate-200">{currencySymbol}{fireTargets.fireNumberToday.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span></div>
                                <div className="flex justify-between"><span>Liquid FIRE progress today</span><span className="text-emerald-300">{liquidFireProgressPct.toFixed(2)}%</span></div>
                                <div className="flex justify-between"><span>Total net worth progress today</span><span className="text-sky-300">{totalNetWorthProgressPct.toFixed(1)}%</span></div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-[#1e1e26] p-6 rounded-2xl border border-white/5 shadow-lg flex flex-col justify-between">
                        <div>
                            <div className="text-slate-500 text-xs font-bold uppercase mb-2">Snapshot: At Retirement</div>
                            <div className="text-3xl font-bold text-white mb-1">
                                {currencySymbol}{retirementNetWorth.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                            </div>
                            <div className="flex items-center space-x-2 text-xs text-slate-400">
                                <span>Age {retirementAgeTarget}</span>
                            </div>
                            <div className="mt-3 space-y-1 text-xs text-slate-400">
                                <div className="flex justify-between"><span>Projected liquid FIRE assets</span><span className="text-emerald-300">{currencySymbol}{retirementLiquid.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span></div>
                                <div className="flex justify-between"><span>Projected pension assets</span><span className="text-slate-200">{currencySymbol}{projectedPensionAssetsAtRetirement.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span></div>
                                <div className="flex justify-between"><span>Projected property/home equity</span><span className="text-slate-200">{currencySymbol}{projectedPropertyAssetsAtRetirement.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span></div>
                                <div className="flex justify-between"><span>Projected other illiquid assets</span><span className="text-slate-200">{currencySymbol}{retirementOtherAssets.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span></div>
                                <div className="flex justify-between"><span>Projected liabilities/debt</span><span className="text-rose-300">{currencySymbol}{retirementDebt.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span></div>
                                <div className="flex justify-between"><span>Projected total net worth</span><span className="text-slate-200">{currencySymbol}{retirementNetWorth.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span></div>
                                <div className="flex justify-between"><span>Projected retirement expenses</span><span className="text-slate-200">{currencySymbol}{fireTargets.projectedRetirementExpensesAtRetirementAge.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span></div>
                                <div className="flex justify-between"><span>FIRE target at retirement</span><span className="text-slate-200">{currencySymbol}{fireNumberAtRetirement.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span></div>
                                <div className="flex justify-between"><span>Liquid FIRE progress</span><span className="text-emerald-300">{(retirementReadiness.liquidFireProgressAtRetirementPct * 100).toFixed(1)}%</span></div>
                                <div className="flex justify-between"><span>Retirement income coverage</span><span className={retirementReadiness.isFireReady ? 'text-emerald-300' : 'text-rose-300'}>{(retirementReadiness.incomeCoveragePct * 100).toFixed(0)}%</span></div>
                            </div>
                        </div>
                        <div className="mt-4">
                            <div className="w-full bg-slate-800 h-2 rounded-full overflow-hidden">
                                <div className={retirementReadiness.isFireReady ? 'bg-emerald-400 h-full' : 'bg-rose-400 h-full'} style={{ width: `${Math.max(0, Math.min(100, retirementReadiness.incomeCoveragePct * 100))}%` }}></div>
                            </div>
                            <div className="text-right text-xs text-sky-300 mt-1 font-medium">
                                {retirementReadiness.message}
                            </div>
                            {retirementDebt > 0 && (
                                <p className="text-[11px] text-amber-300 mt-1 text-right">Debt remains at retirement.</p>
                            )}
                            {hasDebtGrowingRisk && (
                                <p className="text-[11px] text-amber-300 mt-1 text-right">Debt balance may increase under current payment assumptions.</p>
                            )}
                            <p className="text-[11px] text-slate-400 mt-1 text-right">
                                Readiness is based on liquid FIRE assets plus secure retirement income, not home equity.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Main Chart */}
                <div className="bg-[#1e1e26] p-6 rounded-2xl border border-white/5 shadow-lg">
                    <div className="flex justify-between items-center mb-6">
                        <div>
                            <h3 className="text-white font-bold">Projected Total Net Worth</h3>
                            {(simSavingsAdj !== 0 || simRetirementAdj !== 0) && (
                                <span className="text-xs text-sky-400 ml-2 animate-pulse">Simulator Active</span>
                            )}
                        </div>
                    </div>
                    <div className="h-80 w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={simulationData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="colorBal" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                                    </linearGradient>
                                    <linearGradient id="colorBase" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#64748b" stopOpacity={0.1}/>
                                    <stop offset="95%" stopColor="#64748b" stopOpacity={0}/>
                                    </linearGradient>
                                </defs>
                                <XAxis dataKey="age" stroke="#64748b" />
                                <YAxis stroke="#64748b" tickFormatter={(val) => `${currencySymbol}${val/1000}k`} />
                                <Tooltip content={<CustomTooltip />} cursor={{ stroke: '#64748b', strokeWidth: 1, strokeDasharray: '4 4' }} />
                                <ReferenceLine x={tempConfig.retirementAge + simRetirementAdj} stroke="#f59e0b" strokeDasharray="3 3" label={{ value: 'Retirement', position: 'insideTopRight', fill: '#f59e0b', fontSize: 12 }} />

                                {/* Baseline (Show only if simulator is active) */}
                                {(simSavingsAdj !== 0 || simRetirementAdj !== 0) && (
                                    <Area type="monotone" dataKey="baseBalance" stroke="#64748b" strokeDasharray="5 5" fillOpacity={1} fill="url(#colorBase)" />
                                )}

                                <Area type="monotone" dataKey="liquid" name="Projected Liquid FIRE Assets" stroke="#06b6d4" fillOpacity={0.15} fill="#06b6d4" />
                                <Area type="monotone" dataKey="balance" name="Projected Total Net Worth" stroke="#10b981" fillOpacity={1} fill="url(#colorBal)" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Retirement Paycheck Visualizer */}
                {(preRetirementPaycheck || retirementPaycheck) && (
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                        {preRetirementPaycheck && (
                            <div className="bg-[#1e1e26] p-6 rounded-2xl border border-white/5 shadow-lg">
                                <div className="flex justify-between items-center mb-6">
                                    <div>
                                        <h3 className="text-white font-bold flex items-center">
                                            <Briefcase size={18} className="mr-2 text-sky-400" />
                                            Pre-Retirement Annual Income (Age {preRetirementPaycheck.age})
                                        </h3>
                                        <p className="text-slate-500 text-xs">Working years snapshot including salary and other income.</p>
                                    </div>
                                    <div className={`text-right px-3 py-1 rounded-lg border ${preRetirementPaycheck.surplus >= 0 ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' : 'bg-rose-500/10 border-rose-500/30 text-rose-400'}`}>
                                        <div className="text-xs font-semibold uppercase">Coverage</div>
                                        <div className="text-sm font-bold">
                                            {((preRetirementPaycheck.totalIncome / preRetirementPaycheck.projectedExpenses) * 100).toFixed(0)}%
                                        </div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                    {/* Income Sources List */}
                                    <div className="space-y-4">
                                        {preRetirementPaycheck.sources.map((source, idx) => {
                                            const displayType = source.type === 'Pension' ? 'DB / State Pension' : source.type;

                                            return (
                                                <div key={idx} className="group">
                                                    <div className="flex justify-between items-end mb-1">
                                                        <div className="flex items-center space-x-2">
                                                            <div className="p-1.5 rounded-md bg-white/5">
                                                                {source.name.includes('Dividends') ? <PieChartIcon size={14} className="text-emerald-400"/> :
                                                                 source.name.includes('Withdrawals') ? <TrendingUp size={14} className="text-sky-400"/> :
                                                                 source.type === 'Pension' ? <Wallet size={14} className="text-sky-400"/> :
                                                                 <DollarSign size={14} className="text-slate-400"/>}
                                                            </div>
                                                            <div>
                                                                <div className="text-sm text-slate-200 font-medium">{source.name}</div>
                                                                <div className="text-[10px] text-slate-500">{displayType}</div>
                                                            </div>
                                                        </div>
                                                        <div className="text-right">
                                                            <div className="text-sm font-bold text-white">
                                                                {currencySymbol}{Math.round(source.amount).toLocaleString()}
                                                            </div>
                                                            <div className="text-[10px] text-slate-500">
                                                                {currencySymbol}{Math.round(source.amount / 12).toLocaleString()}/mo
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="w-full bg-slate-800 h-1.5 rounded-full overflow-hidden">
                                                        <div
                                                            className="h-full rounded-full"
                                                            style={{
                                                                width: `${(source.amount / preRetirementPaycheck.totalIncome) * 100}%`,
                                                                backgroundColor: source.color || '#6366f1'
                                                            }}
                                                        ></div>
                                                    </div>
                                                </div>
                                            );
                                        })}

                                        <div className="border-t border-white/10 pt-4 flex justify-between items-center mt-4">
                                            <span className="text-sm font-bold text-white">Total Annual Income</span>
                                            <span className="text-lg font-bold text-white">{currencySymbol}{Math.round(preRetirementPaycheck.totalIncome).toLocaleString()}</span>
                                        </div>
                                    </div>

                                    {/* Comparison Chart */}
                                    <div className="h-64 w-full bg-slate-900/30 rounded-xl border border-white/5 p-4">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart
                                                data={[
                                                    { name: 'Income', value: preRetirementPaycheck.totalIncome },
                                                    { name: 'Expenses', value: preRetirementPaycheck.projectedExpenses }
                                                ]}
                                                layout="vertical"
                                                margin={{ top: 20, right: 50, left: 20, bottom: 5 }}
                                            >
                                                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#334155" />
                                                <XAxis type="number" hide />
                                                <YAxis dataKey="name" type="category" tick={{fill: '#e2e8f0', fontSize: 12}} width={70} />
                                                <Tooltip
                                                    cursor={{fill: 'transparent'}}
                                                    contentStyle={{ backgroundColor: '#1e1e26', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.1)', color: '#fff' }}
                                                    itemStyle={{ color: '#fff' }}
                                                    labelStyle={{ color: '#fff' }}
                                                    formatter={(value: number) => [`${currencySymbol}${Math.round(value).toLocaleString()}`, 'Amount']}
                                                />
                                                <Bar dataKey="value" barSize={30} radius={[0, 4, 4, 0]}>
                                                    {
                                                        [0, 1].map((entry, index) => (
                                                            <Cell key={`cell-${index}`} fill={index === 0 ? (preRetirementPaycheck.surplus >= 0 ? '#10b981' : '#f43f5e') : '#64748b'} />
                                                        ))
                                                    }
                                                    <LabelList
                                                        dataKey="value"
                                                        position="right"
                                                        fill="#ffffff"
                                                        fontSize={12}
                                                        formatter={(value: number) => `${currencySymbol}${Math.round(value).toLocaleString()}`}
                                                    />
                                                </Bar>
                                            </BarChart>
                                        </ResponsiveContainer>
                                        <div className="text-center mt-2">
                                            <p className="text-xs text-slate-300">
                                                Projected Expenses at Age {preRetirementPaycheck.age}: <span className="text-white font-bold">{currencySymbol}{Math.round(preRetirementPaycheck.projectedExpenses).toLocaleString()}</span>
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {retirementPaycheck && (
                            <div className="bg-[#1e1e26] p-6 rounded-2xl border border-white/5 shadow-lg">
                                <div className="flex justify-between items-center mb-6">
                                    <div>
                                        <h3 className="text-white font-bold flex items-center">
                                            <Briefcase size={18} className="mr-2 text-sky-400" />
                                            Post-Retirement Annual Income (Age {tempConfig.retirementAge + simRetirementAdj})
                                        </h3>
                                        <p className="text-slate-500 text-xs">Projected annual cash flow from all sources.</p>
                                    </div>
                                    <div className={`text-right px-3 py-1 rounded-lg border ${retirementPaycheck.surplus >= 0 ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' : 'bg-rose-500/10 border-rose-500/30 text-rose-400'}`}>
                                        <div className="text-xs font-semibold uppercase">Coverage</div>
                                        <div className="text-sm font-bold">
                                            {((retirementPaycheck.totalIncome / retirementPaycheck.projectedExpenses) * 100).toFixed(0)}%
                                        </div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                    {/* Income Sources List */}
                                    <div className="space-y-4">
                                        {retirementPaycheck.sources.map((source, idx) => {
                                            const displayType = source.type === 'Pension' ? 'DB / State Pension' : source.type;

                                            return (
                                                <div key={idx} className="group">
                                                    <div className="flex justify-between items-end mb-1">
                                                        <div className="flex items-center space-x-2">
                                                            <div className="p-1.5 rounded-md bg-white/5">
                                                                {source.name.includes('Dividends') ? <PieChartIcon size={14} className="text-emerald-400"/> :
                                                                 source.name.includes('Withdrawals') ? <TrendingUp size={14} className="text-sky-400"/> :
                                                                 source.type === 'Pension' ? <Wallet size={14} className="text-sky-400"/> :
                                                                 <DollarSign size={14} className="text-slate-400"/>}
                                                            </div>
                                                            <div>
                                                                <div className="text-sm text-slate-200 font-medium">{source.name}</div>
                                                                <div className="text-[10px] text-slate-500">{displayType}</div>
                                                            </div>
                                                        </div>
                                                        <div className="text-right">
                                                            <div className="text-sm font-bold text-white">
                                                                {currencySymbol}{Math.round(source.amount).toLocaleString()}
                                                            </div>
                                                            <div className="text-[10px] text-slate-500">
                                                                {currencySymbol}{Math.round(source.amount / 12).toLocaleString()}/mo
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="w-full bg-slate-800 h-1.5 rounded-full overflow-hidden">
                                                        <div
                                                            className="h-full rounded-full"
                                                            style={{
                                                                width: `${(source.amount / retirementPaycheck.totalIncome) * 100}%`,
                                                                backgroundColor: source.color || '#6366f1'
                                                            }}
                                                        ></div>
                                                    </div>
                                                </div>
                                            );
                                        })}

                                        <div className="border-t border-white/10 pt-4 flex justify-between items-center mt-4">
                                            <span className="text-sm font-bold text-white">Total Annual Income</span>
                                            <span className="text-lg font-bold text-white">{currencySymbol}{Math.round(retirementPaycheck.totalIncome).toLocaleString()}</span>
                                        </div>
                                    </div>

                                    {/* Comparison Chart */}
                                    <div className="h-64 w-full bg-slate-900/30 rounded-xl border border-white/5 p-4">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart
                                                data={[
                                                    { name: 'Income', value: retirementPaycheck.totalIncome },
                                                    { name: 'Expenses', value: retirementPaycheck.projectedExpenses }
                                                ]}
                                                layout="vertical"
                                                margin={{ top: 20, right: 50, left: 20, bottom: 5 }}
                                            >
                                                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#334155" />
                                                <XAxis type="number" hide />
                                                <YAxis dataKey="name" type="category" tick={{fill: '#e2e8f0', fontSize: 12}} width={70} />
                                                <Tooltip
                                                    cursor={{fill: 'transparent'}}
                                                    contentStyle={{ backgroundColor: '#1e1e26', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.1)', color: '#fff' }}
                                                    itemStyle={{ color: '#fff' }}
                                                    labelStyle={{ color: '#fff' }}
                                                    formatter={(value: number) => [`${currencySymbol}${Math.round(value).toLocaleString()}`, 'Amount']}
                                                />
                                                <Bar dataKey="value" barSize={30} radius={[0, 4, 4, 0]}>
                                                    {
                                                        [0, 1].map((entry, index) => (
                                                            <Cell key={`cell-${index}`} fill={index === 0 ? (retirementPaycheck.surplus >= 0 ? '#10b981' : '#f43f5e') : '#64748b'} />
                                                        ))
                                                    }
                                                    <LabelList
                                                        dataKey="value"
                                                        position="right"
                                                        fill="#ffffff"
                                                        fontSize={12}
                                                        formatter={(value: number) => `${currencySymbol}${Math.round(value).toLocaleString()}`}
                                                    />
                                                </Bar>
                                            </BarChart>
                                        </ResponsiveContainer>
                                        <div className="text-center mt-2">
                                            <p className="text-xs text-slate-300">
                                                Projected Expenses at Age {tempConfig.retirementAge + simRetirementAdj}: <span className="text-white font-bold">{currencySymbol}{Math.round(retirementPaycheck.projectedExpenses).toLocaleString()}</span>
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* FIRE Gap Breakdown */}
                <div className="bg-[#1e1e26] p-6 rounded-2xl border border-white/5 shadow-lg">
                    <h3 className="text-slate-300 font-bold text-sm mb-4 flex items-center">
                        <Target size={16} className="text-sky-400 mr-2" />
                        FIRE Gap Breakdown
                    </h3>
                    <div className="space-y-3">
                        {[
                            Number.isFinite(fireNumberAtRetirement) ? {
                                label: 'FIRE target at retirement',
                                value: `${currencySymbol}${Math.round(fireNumberAtRetirement).toLocaleString()}`,
                                valueClass: 'text-slate-200'
                            } : null,
                            Number.isFinite(retirementReadiness.projectedLiquidFireAssetsAtRetirement) ? {
                                label: 'Projected liquid FIRE assets at retirement',
                                value: `${currencySymbol}${Math.round(retirementReadiness.projectedLiquidFireAssetsAtRetirement).toLocaleString()}`,
                                valueClass: 'text-emerald-300'
                            } : null,
                            Number.isFinite(gapToTarget) ? {
                                label: 'Liquid FIRE funding gap',
                                value: `${currencySymbol}${Math.round(gapToTarget).toLocaleString()}`,
                                valueClass: gapToTarget > 0 ? 'text-rose-300' : 'text-emerald-300'
                            } : null,
                            Number.isFinite(requiredExtraMonthly) ? {
                                label: 'Required extra monthly savings',
                                value: `${currencySymbol}${Math.round(requiredExtraMonthly).toLocaleString()}/mo`,
                                valueClass: requiredExtraMonthly > 0 ? 'text-amber-300' : 'text-emerald-300'
                            } : null,
                            Number.isFinite(retirementReadiness.incomeCoveragePct) ? {
                                label: 'Retirement income coverage',
                                value: `${(retirementReadiness.incomeCoveragePct * 100).toFixed(0)}%`,
                                valueClass: retirementReadiness.isFireReady ? 'text-emerald-300' : 'text-rose-300'
                            } : null
                        ].filter((row): row is { label: string; value: string; valueClass: string } => row !== null).map((row) => (
                            <div key={row.label} className="flex items-center justify-between border-b border-white/5 pb-3 last:border-b-0 last:pb-0 gap-4">
                                <span className="text-xs text-slate-400">{row.label}</span>
                                <span className={`text-sm font-bold text-right ${row.valueClass}`}>{row.value}</span>
                            </div>
                        ))}
                        {![
                            fireNumberAtRetirement,
                            retirementReadiness.projectedLiquidFireAssetsAtRetirement,
                            gapToTarget,
                            requiredExtraMonthly,
                            retirementReadiness.incomeCoveragePct
                        ].some(Number.isFinite) && (
                            <div className="text-slate-500 text-sm">No FIRE gap data available yet.</div>
                        )}
                    </div>
                </div>

                {/* Wealth Drivers (Growth) */}
                <div className="bg-[#1e1e26] p-6 rounded-2xl border border-white/5 shadow-lg">
                    <h3 className="text-slate-300 font-bold text-sm mb-4 flex items-center">
                        <TrendingUp size={16} className="text-sky-400 mr-2" />
                        Wealth Drivers (Annual Growth)
                    </h3>
                    <div className="space-y-3">
                        {wealthDrivers.map((driver, index) => (
                            <div key={index} className="flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                    <div className="w-2 h-8 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }}></div>
                                    <div>
                                        <div className="text-sm text-white font-medium">{driver.name}</div>
                                        <div className="text-xs text-slate-500">{driver.rate}% Growth Rate</div>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="text-sm font-bold text-emerald-400">+{currencySymbol}{Math.round(driver.amount).toLocaleString()}</div>
                                    <div className="text-[10px] text-slate-500">/year</div>
                                </div>
                            </div>
                        ))}
                        <div className="border-t border-white/5 pt-3 mt-2 flex justify-between items-center">
                            <span className="text-sm font-bold text-slate-300">Total Passive Growth</span>
                            <span className="text-lg font-bold text-emerald-400">
                                +{currencySymbol}{Math.round(wealthDrivers.reduce((s,d)=>s+d.amount,0)).toLocaleString()}
                            </span>
                        </div>
                    </div>
                </div>

                {/* AI Advisor */}
                <div className="bg-gradient-to-br from-sky-900/20 to-purple-900/20 border border-white/10 rounded-2xl p-6">
                    <div className="flex justify-between items-center mb-4">
                        <div className="flex items-center space-x-2">
                            <Sparkles size={18} className="text-sky-400" />
                            <h3 className="text-white font-bold">AI FIRE Coach</h3>
                        </div>
                        <button
                            onClick={handleAnalyzeFire}
                            disabled={isAnalyzing}
                            className="text-xs bg-sky-600 hover:bg-sky-500 px-3 py-1.5 rounded-lg text-white transition-colors disabled:opacity-50 flex items-center"
                        >
                            {isAnalyzing ? <RefreshCw size={12} className="animate-spin mr-1"/> : <Sparkles size={12} className="mr-1"/>}
                            {isAnalyzing ? 'Analyzing...' : 'Generate Plan'}
                        </button>
                    </div>
                    {aiAnalysis ? (
                        <div className="prose prose-invert prose-sm max-w-none text-slate-300" dangerouslySetInnerHTML={{ __html: aiAnalysis }} />
                    ) : (
                        <p className="text-sm text-slate-500">
                            Ask AI to review your Net Worth, Income Streams, and FIRE Goals to identify gaps and suggest optimizations.
                        </p>
                    )}
                </div>
            </div>
        )}

        {/* --- STRATEGY TAB --- */}
        {activeTab === 'strategy' && (
            <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Basic Info */}
                    <div className="bg-[#1e1e26] p-6 rounded-2xl border border-white/5">
                        <h3 className="text-white font-bold mb-4 flex items-center"><Settings2 size={18} className="mr-2 text-sky-400"/> Parameters</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="text-xs text-slate-400 block mb-1">Current Age</label>
                                <input type="number" value={tempConfig.currentAge} onChange={(e) => setTempConfig({...tempConfig, currentAge: +e.target.value})} className="w-full bg-[#13131a] border border-white/10 rounded-lg px-3 py-2 text-white focus:border-sky-500 outline-none transition-colors" />
                                <span className="text-[10px] text-slate-500 block mt-1">For couples, use primary planner's age.</span>
                            </div>
                            <div>
                                <label className="text-xs text-slate-400 block mb-1">Target Retirement Age</label>
                                <input type="number" value={tempConfig.retirementAge} onChange={(e) => setTempConfig({...tempConfig, retirementAge: +e.target.value})} className="w-full bg-[#13131a] border border-white/10 rounded-lg px-3 py-2 text-white focus:border-sky-500 outline-none transition-colors" />
                            </div>
                            <div>
                                <label className="text-xs text-slate-400 block mb-1">Life Expectancy</label>
                                <input type="number" value={tempConfig.lifeExpectancy} onChange={(e) => setTempConfig({...tempConfig, lifeExpectancy: +e.target.value})} className="w-full bg-[#13131a] border border-white/10 rounded-lg px-3 py-2 text-white focus:border-sky-500 outline-none transition-colors" />
                            </div>
                        </div>
                    </div>

                    {/* Assumptions */}
                    <div className="bg-[#1e1e26] p-6 rounded-2xl border border-white/5">
                        <h3 className="text-white font-bold mb-4 flex items-center"><TrendingUp size={18} className="mr-2 text-emerald-400"/> Economic Assumptions</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="text-xs text-slate-400 block mb-1">Investment Return Rate (%)</label>
                                <input type="number" step="0.1" value={tempConfig.investmentReturnRate} onChange={(e) => setTempConfig({...tempConfig, investmentReturnRate: +e.target.value})} className="w-full bg-[#13131a] border border-white/10 rounded-lg px-3 py-2 text-white focus:border-sky-500 outline-none transition-colors" />
                            </div>
                            <div>
                                <label className="text-xs text-slate-400 block mb-1">Inflation Rate (%)</label>
                                <input type="number" step="0.1" value={tempConfig.inflationRate} onChange={(e) => setTempConfig({...tempConfig, inflationRate: +e.target.value})} className="w-full bg-[#13131a] border border-white/10 rounded-lg px-3 py-2 text-white focus:border-sky-500 outline-none transition-colors" />
                            </div>
                            <div>
                                <label className="text-xs text-slate-400 block mb-1">Income Growth Rate (%)</label>
                                <input type="number" step="0.1" value={tempConfig.incomeGrowthRate} onChange={(e) => setTempConfig({...tempConfig, incomeGrowthRate: +e.target.value})} className="w-full bg-[#13131a] border border-white/10 rounded-lg px-3 py-2 text-white focus:border-sky-500 outline-none transition-colors" />
                                <span className="text-[10px] text-slate-500 block mt-1">Applies to salary, rental, and other income streams.</span>
                            </div>
                            <div>
                                <label className="text-xs text-slate-400 block mb-1">Safe Withdrawal Rate (%)</label>
                                <input type="number" step="0.1" value={tempConfig.withdrawalRate} onChange={(e) => setTempConfig({...tempConfig, withdrawalRate: +e.target.value})} className="w-full bg-[#13131a] border border-white/10 rounded-lg px-3 py-2 text-white focus:border-sky-500 outline-none transition-colors" />
                            </div>
                        </div>
                    </div>

                    {/* Lifestyle */}
                    <div className="bg-[#1e1e26] p-6 rounded-2xl border border-white/5 md:col-span-2">
                        <h3 className="text-white font-bold mb-4 flex items-center"><Heart size={18} className="mr-2 text-rose-400"/> Lifestyle & Goals</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="text-xs text-slate-400 block mb-1">Current Annual Expenses</label>
                                <div className="relative">
                                    <span className="absolute left-3 top-2 text-slate-500">{currencySymbol}</span>
                                    <input type="number" value={tempConfig.annualExpenses} onChange={(e) => setTempConfig({...tempConfig, annualExpenses: +e.target.value})} className="w-full bg-[#13131a] border border-white/10 rounded-lg pl-8 pr-3 py-2 text-white focus:border-sky-500 outline-none transition-colors" />
                                </div>
                                <span className="text-[10px] text-slate-500 mt-1">Combine household spending.</span>
                            </div>
                            <div>
                                <label className="text-xs text-emerald-400 font-bold block mb-1">Target Retirement Expenses</label>
                                <div className="relative">
                                    <span className="absolute left-3 top-2 text-slate-500">{currencySymbol}</span>
                                    <input
                                        type="number"
                                        value={tempConfig.retirementExpenses || tempConfig.annualExpenses}
                                        onChange={(e) => setTempConfig({...tempConfig, retirementExpenses: +e.target.value})}
                                        className="w-full bg-[#13131a] border border-emerald-500/30 rounded-lg pl-8 pr-3 py-2 text-white focus:border-emerald-500 outline-none transition-colors"
                                    />
                                </div>
                                <span className="text-[10px] text-slate-500 mt-1">Future lifestyle cost (e.g. moving abroad).</span>
                            </div>
                            <div>
                                <label className="text-xs text-slate-400 block mb-1">FIRE Style</label>
                                <select
                                    value={tempConfig.fireStyle}
                                    onChange={(e) => setTempConfig({...tempConfig, fireStyle: e.target.value as FireStyle})}
                                    className="w-full bg-[#13131a] border border-white/10 rounded-lg px-3 py-2 text-white focus:border-sky-500 outline-none transition-colors"
                                >
                                    <option value="Lean">Lean FIRE (Minimalist)</option>
                                    <option value="Traditional">Traditional FIRE</option>
                                    <option value="Fat">Fat FIRE (Luxury)</option>
                                    <option value="Coast">Coast FIRE (Stop saving, let grow)</option>
                                    <option value="Barista">Barista FIRE (Part-time work)</option>
                                </select>
                            </div>
                            <div>
                                <label className="text-xs text-slate-400 block mb-1">My "Why" (Motivation)</label>
                                <textarea
                                    value={tempConfig.lifestyleGoals}
                                    onChange={(e) => setTempConfig({...tempConfig, lifestyleGoals: e.target.value})}
                                    placeholder="e.g. Travel the world, spend time with family, start a non-profit..."
                                    className="w-full bg-[#13131a] border border-white/10 rounded-lg px-3 py-2 text-white focus:border-sky-500 outline-none transition-colors h-24 resize-none"
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="flex justify-end">
                    <button onClick={handleSave} className="flex items-center space-x-2 bg-sky-600 hover:bg-sky-500 text-white px-6 py-3 rounded-xl font-medium shadow-lg shadow-sky-500/20 transition-all">
                        <Save size={18} />
                        <span>Save Strategy</span>
                    </button>
                </div>
            </div>
        )}

        {/* --- NET WORTH TAB --- */}
        {activeTab === 'networth' && (
            <div className="space-y-6">
                {/* Assets */}
                <div className="bg-[#1e1e26] p-6 rounded-2xl border border-white/5">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-white font-bold flex items-center"><Building size={18} className="mr-2 text-sky-400"/> Assets (Non-Portfolio)</h3>
                        <button
                            onClick={() => setTempConfig({...tempConfig, manualAssets: [...tempConfig.manualAssets, { id: Date.now().toString(), name: 'New Asset', value: 0, growthRate: 3, type: 'Other' }]})}
                            className="text-xs bg-[#13131a] hover:bg-[#2a2a35] text-sky-400 px-3 py-1.5 rounded-lg border border-white/10 transition-colors flex items-center"
                        >
                            <Plus size={14} className="mr-1"/> Add Asset
                        </button>
                    </div>
                    <div className="space-y-3">
                        {tempConfig.manualAssets.map((asset, index) => (
                            <div key={asset.id} className="grid grid-cols-12 gap-2 items-center bg-[#13131a] p-3 rounded-xl border border-white/5">
                                <div className="col-span-3">
                                    <input
                                        type="text"
                                        value={asset.name}
                                        onChange={(e) => {
                                            const newAssets = [...tempConfig.manualAssets];
                                            newAssets[index].name = e.target.value;
                                            setTempConfig({...tempConfig, manualAssets: newAssets});
                                        }}
                                        className="w-full bg-transparent text-sm text-white focus:outline-none placeholder-slate-600"
                                        placeholder="Asset Name"
                                    />
                                </div>
                                <div className="col-span-3">
                                    <select
                                        value={asset.type}
                                        onChange={(e) => {
                                            const newAssets = [...tempConfig.manualAssets];
                                            newAssets[index].type = e.target.value as any;
                                            setTempConfig({...tempConfig, manualAssets: newAssets});
                                        }}
                                        className="w-full bg-[#1e1e26] text-xs text-slate-300 rounded px-2 py-1 border-none focus:ring-0"
                                    >
                                        <option value="Real Estate">Real Estate</option>
                                        <option value="Cash">Cash / Savings</option>
                                        <option value="Crypto">Crypto (Manual)</option>
                                        <option value="ISA">ISA</option>
                                        <option value="GIA">GIA</option>
                                        <option value="Pension">Pension Pot (DC / Workplace)</option>
                                        <option value="Other">Other</option>
                                    </select>
                                </div>
                                <div className="col-span-3 relative">
                                    <span className="absolute left-2 top-1.5 text-slate-500 text-xs">{currencySymbol}</span>
                                    <input
                                        type="number"
                                        value={asset.value}
                                        onChange={(e) => {
                                            const newAssets = [...tempConfig.manualAssets];
                                            newAssets[index].value = +e.target.value;
                                            setTempConfig({...tempConfig, manualAssets: newAssets});
                                        }}
                                        className="w-full bg-[#1e1e26] rounded px-2 pl-6 py-1 text-sm text-white border-none focus:ring-0"
                                    />
                                </div>
                                <div className="col-span-2 flex items-center">
                                    <input
                                        type="number"
                                        value={asset.growthRate}
                                        onChange={(e) => {
                                            const newAssets = [...tempConfig.manualAssets];
                                            newAssets[index].growthRate = +e.target.value;
                                            setTempConfig({...tempConfig, manualAssets: newAssets});
                                        }}
                                        className="w-12 bg-[#1e1e26] rounded px-1 py-1 text-sm text-white text-center border-none focus:ring-0 mr-1"
                                    />
                                    <span className="text-xs text-slate-500">% grwth</span>
                                </div>
                                <div className="col-span-1 text-right">
                                    <button
                                        onClick={() => {
                                            const newAssets = tempConfig.manualAssets.filter((_, i) => i !== index);
                                            setTempConfig({...tempConfig, manualAssets: newAssets});
                                        }}
                                        className="text-rose-400 hover:bg-rose-500/10 p-1.5 rounded-lg transition-colors"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            </div>
                        ))}
                        {tempConfig.manualAssets.length === 0 && (
                            <p className="text-sm text-slate-500 text-center py-4">No manual assets added (e.g. Home, Cash).</p>
                        )}
                    </div>
                </div>

                {/* Liabilities */}
                <div className="bg-[#1e1e26] p-6 rounded-2xl border border-white/5">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-white font-bold flex items-center"><CreditCard size={18} className="mr-2 text-rose-400"/> Liabilities (Debts)</h3>
                        <button
                            onClick={() => setTempConfig({...tempConfig, liabilities: [...tempConfig.liabilities, { id: Date.now().toString(), name: 'New Debt', balance: 0, interestRate: 5, monthlyPayment: 0 }]})}
                            className="text-xs bg-[#13131a] hover:bg-[#2a2a35] text-rose-400 px-3 py-1.5 rounded-lg border border-white/10 transition-colors flex items-center"
                        >
                            <Plus size={14} className="mr-1"/> Add Debt
                        </button>
                    </div>
                    <div className="space-y-3">
                        {tempConfig.liabilities.map((liability, index) => {
                            const monthlyInterest = (liability.balance * (liability.interestRate / 100)) / 12;
                            const showInterestWarning =
                                liability.balance > 0 && liability.monthlyPayment < monthlyInterest;

                            return (
                                <div key={liability.id} className="space-y-2">
                                    <div className="grid grid-cols-12 gap-2 items-center bg-[#13131a] p-3 rounded-xl border border-white/5">
                                        <div className="col-span-3">
                                            <input
                                                type="text"
                                                value={liability.name}
                                                onChange={(e) => {
                                                    const newLiabs = [...tempConfig.liabilities];
                                                    newLiabs[index].name = e.target.value;
                                                    setTempConfig({...tempConfig, liabilities: newLiabs});
                                                }}
                                                className="w-full bg-transparent text-sm text-white focus:outline-none placeholder-slate-600"
                                                placeholder="Debt Name"
                                            />
                                        </div>
                                        <div className="col-span-3 relative">
                                            <span className="absolute left-2 top-1.5 text-slate-500 text-xs">{currencySymbol}</span>
                                            <input
                                                type="number"
                                                placeholder="Balance"
                                                value={liability.balance}
                                                onChange={(e) => {
                                                    const newLiabs = [...tempConfig.liabilities];
                                                    newLiabs[index].balance = +e.target.value;
                                                    setTempConfig({...tempConfig, liabilities: newLiabs});
                                                }}
                                                className="w-full bg-[#1e1e26] rounded px-2 pl-6 py-1 text-sm text-white border-none focus:ring-0"
                                            />
                                        </div>
                                        <div className="col-span-2 flex items-center">
                                            <input
                                                type="number"
                                                value={liability.interestRate}
                                                onChange={(e) => {
                                                    const newLiabs = [...tempConfig.liabilities];
                                                    newLiabs[index].interestRate = +e.target.value;
                                                    setTempConfig({...tempConfig, liabilities: newLiabs});
                                                }}
                                                className="w-12 bg-[#1e1e26] rounded px-1 py-1 text-sm text-white text-center border-none focus:ring-0 mr-1"
                                            />
                                            <span className="text-xs text-slate-500">% APR</span>
                                        </div>
                                        <div className="col-span-3 relative">
                                            <span className="absolute left-2 top-1.5 text-slate-500 text-xs">{currencySymbol}</span>
                                            <input
                                                type="number"
                                                placeholder="Monthly payment (£/mo)"
                                                value={liability.monthlyPayment}
                                                onChange={(e) => {
                                                    const newLiabs = [...tempConfig.liabilities];
                                                    newLiabs[index].monthlyPayment = +e.target.value;
                                                    setTempConfig({...tempConfig, liabilities: newLiabs});
                                                }}
                                                className="w-full bg-[#1e1e26] rounded px-2 pl-6 py-1 text-sm text-white border-none focus:ring-0"
                                            />
                                        </div>
                                        <div className="col-span-1 text-right">
                                            <button
                                                onClick={() => {
                                                    const newLiabs = tempConfig.liabilities.filter((_, i) => i !== index);
                                                    setTempConfig({...tempConfig, liabilities: newLiabs});
                                                }}
                                                className="text-rose-400 hover:bg-rose-500/10 p-1.5 rounded-lg transition-colors"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </div>
                                    {showInterestWarning && (
                                        <div className="text-[11px] text-amber-400 px-3">
                                            Payment is below monthly interest ({currencySymbol}{Math.round(monthlyInterest).toLocaleString()}). Debt will increase.
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                        {tempConfig.liabilities.length === 0 && (
                            <p className="text-sm text-slate-500 text-center py-4">No liabilities added. Great job!</p>
                        )}
                    </div>
                </div>
                <div className="flex justify-end">
                    <button onClick={handleSave} className="flex items-center space-x-2 bg-sky-600 hover:bg-sky-500 text-white px-6 py-3 rounded-xl font-medium shadow-lg shadow-sky-500/20 transition-all">
                        <Save size={18} />
                        <span>Save Net Worth</span>
                    </button>
                </div>
            </div>
        )}

        {/* --- INCOME TAB --- */}
        {activeTab === 'income' && (
            <div className="bg-[#1e1e26] p-6 rounded-2xl border border-white/5">
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h3 className="text-white font-bold flex items-center"><PiggyBank size={18} className="mr-2 text-emerald-400"/> Income Streams</h3>
                        <p className="text-xs text-slate-500 mt-1">Define current and future income sources (e.g. Salary, Rental, DB/State Pension, Annuity). Amounts are entered in today’s money and projected forward using the income growth rate.</p>
                    </div>
                    <button
                        onClick={() => setTempConfig({...tempConfig, incomeStreams: [...tempConfig.incomeStreams, { id: Date.now().toString(), name: 'New Stream', amount: 0, type: 'Salary', startAge: tempConfig.currentAge, endAge: tempConfig.retirementAge }]})}
                        className="text-xs bg-[#13131a] hover:bg-[#2a2a35] text-emerald-400 px-3 py-1.5 rounded-lg border border-white/10 transition-colors flex items-center"
                    >
                        <Plus size={14} className="mr-1"/> Add Stream
                    </button>
                </div>
                <div className="space-y-4">
                    {tempConfig.incomeStreams.map((stream, index) => (
                        <div key={stream.id} className="grid grid-cols-12 gap-3 items-center bg-[#13131a] p-4 rounded-xl border border-white/5">
                            <div className="col-span-12 md:col-span-3">
                                <label className="text-[10px] text-slate-500 uppercase font-bold block mb-1">Name</label>
                                <input
                                    type="text"
                                    value={stream.name}
                                    onChange={(e) => {
                                        const newStreams = [...tempConfig.incomeStreams];
                                        newStreams[index].name = e.target.value;
                                        setTempConfig({...tempConfig, incomeStreams: newStreams});
                                    }}
                                    className="w-full bg-transparent text-sm text-white focus:outline-none placeholder-slate-600"
                                    placeholder="e.g. Main Job"
                                />
                            </div>
                            <div className="col-span-6 md:col-span-2">
                                <label className="text-[10px] text-slate-500 uppercase font-bold block mb-1">Type</label>
                                <select
                                    value={stream.type}
                                    onChange={(e) => {
                                        const newStreams = [...tempConfig.incomeStreams];
                                        newStreams[index].type = e.target.value as any;
                                        setTempConfig({...tempConfig, incomeStreams: newStreams});
                                    }}
                                    className="w-full bg-[#1e1e26] text-xs text-slate-300 rounded px-2 py-1.5 border-none focus:ring-0"
                                >
                                    <option value="Salary">Salary</option>
                                    <option value="Side-Hustle">Side-Hustle</option>
                                    <option value="Rental">Rental</option>
                                    <option value="Pension">DB / State Pension</option>
                                    <option value="Annuity">Annuity</option>
                                    <option value="Other">Other</option>
                                </select>
                            </div>
                            <div className="col-span-6 md:col-span-3 relative">
                                <label className="text-[10px] text-slate-500 uppercase font-bold block mb-1">Annual Amount</label>
                                <span className="absolute left-2 top-[26px] text-slate-500 text-xs">{currencySymbol}</span>
                                <input
                                    type="number"
                                    value={stream.amount}
                                    onChange={(e) => {
                                        const newStreams = [...tempConfig.incomeStreams];
                                        newStreams[index].amount = +e.target.value;
                                        setTempConfig({...tempConfig, incomeStreams: newStreams});
                                    }}
                                    className="w-full bg-[#1e1e26] rounded px-2 pl-6 py-1.5 text-sm text-white border-none focus:ring-0"
                                />
                            </div>
                            <div className="col-span-5 md:col-span-3 flex items-center space-x-2">
                                <div>
                                    <label className="text-[10px] text-slate-500 uppercase font-bold block mb-1">From Age</label>
                                    <input
                                        type="number"
                                        value={stream.startAge}
                                        onChange={(e) => {
                                            const newStreams = [...tempConfig.incomeStreams];
                                            newStreams[index].startAge = +e.target.value;
                                            setTempConfig({...tempConfig, incomeStreams: newStreams});
                                        }}
                                        className="w-full bg-[#1e1e26] rounded px-2 py-1.5 text-sm text-white text-center border-none focus:ring-0"
                                    />
                                </div>
                                <span className="text-slate-500 mt-4">-</span>
                                <div>
                                    <label className="text-[10px] text-slate-500 uppercase font-bold block mb-1">To Age</label>
                                    <input
                                        type="number"
                                        value={stream.endAge}
                                        onChange={(e) => {
                                            const newStreams = [...tempConfig.incomeStreams];
                                            newStreams[index].endAge = +e.target.value;
                                            setTempConfig({...tempConfig, incomeStreams: newStreams});
                                        }}
                                        className="w-full bg-[#1e1e26] rounded px-2 py-1.5 text-sm text-white text-center border-none focus:ring-0"
                                    />
                                </div>
                            </div>
                            <div className="col-span-1 text-right mt-4">
                                <button
                                    onClick={() => {
                                        const newStreams = tempConfig.incomeStreams.filter((_, i) => i !== index);
                                        setTempConfig({...tempConfig, incomeStreams: newStreams});
                                    }}
                                    className="text-rose-400 hover:bg-rose-500/10 p-1.5 rounded-lg transition-colors"
                                >
                                    <Trash2 size={16} />
                                </button>
                            </div>
                        </div>
                    ))}
                    {tempConfig.incomeStreams.length === 0 && (
                        <p className="text-sm text-slate-500 text-center py-8 border-2 border-dashed border-white/5 rounded-xl">
                            No income streams defined. Add your Salary to start.
                        </p>
                    )}
                </div>
                <div className="flex justify-end mt-6">
                    <button onClick={handleSave} className="flex items-center space-x-2 bg-sky-600 hover:bg-sky-500 text-white px-6 py-3 rounded-xl font-medium shadow-lg shadow-sky-500/20 transition-all">
                        <Save size={18} />
                        <span>Save Streams</span>
                    </button>
                </div>
            </div>
        )}
    </div>
  );
};

export default FireSnapshot;
